import { i18n } from "@tellescope/i18n";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";

export type TicketStage = TellescopeModelFromName<"tickets">["stage"];

export interface StageConfig {
  label: string;
  bgColor: string;
  textColor: string;
}

export interface DashboardStageConfig extends StageConfig {
  status: "default" | "secondary" | "destructive" | "outline";
  customStatusStyles: string;
}

const stageConfigs: Record<string, StageConfig> = {
  "in progress": {
    label: "In Progress",
    bgColor: "bg-yellow-200",
    textColor: "text-yellow-900",
  },
  blocked: {
    label: "Blocked",
    bgColor: "bg-red-500",
    textColor: "text-red-50",
  },
  archived: {
    label: "Archived",
    bgColor: "bg-amber-700",
    textColor: "text-amber-50",
  },
  completed: {
    label: "Completed",
    bgColor: "bg-green-500",
    textColor: "text-green-50",
  },
  "not started": {
    label: "Not Started",
    bgColor: "bg-muted",
    textColor: "text-muted-foreground",
  },
};

export const getStageConfig = (stage?: TicketStage): StageConfig => {
  const stageLower = (stage || i18n.t("Not Started", { ns: "tickets" })).toLowerCase();
  return (
    stageConfigs[stageLower] || {
      label: stage || i18n.t("Not Started", { ns: "tickets" }),
      bgColor: "bg-muted",
      textColor: "text-muted-foreground",
    }
  );
};

export const getDashboardStageConfig = (stage?: TicketStage): DashboardStageConfig => {
  const baseConfig = getStageConfig(stage);
  const stageLower = (stage || "Not Started").toLowerCase();

  let status: "default" | "secondary" | "destructive" | "outline" = "default";

  switch (stageLower) {
    case "in progress":
      status = "secondary";
      break;
    case "blocked":
      status = "destructive";
      break;
    case "archived":
    case "completed":
      status = "outline";
      break;
    case "not started":
    default:
      status = "default";
  }

  return {
    ...baseConfig,
    status,
    customStatusStyles: `${baseConfig.bgColor} ${baseConfig.textColor} border-transparent`,
  };
};
